import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { IPlanOption, ISelfPayRate, IStateData } from '@sondermind/cost-estimate-core';
import { HttpService } from '@sondermind/http';

/** Unauthenticated reference pricing on the monolith, under `enginelessBase`. */
const COST_ESTIMATES_PATH = '/sondermind/billing/cost_estimates';

/** The state payload as the monolith sends it: `self_pay`, and possibly neither key. */
interface IStateDataResponse {
  plans?: IPlanOption[];
  /* eslint-disable-next-line @typescript-eslint/naming-convention */
  self_pay?: ISelfPayRate | null;
}

/**
 * Reads cost-estimate reference pricing from the monolith.
 *
 * Restates the two URLs `@sondermind/cost-estimate-core` also knows, because core's `fetchStateData`
 * pins the prod origin, bypasses `HttpService`, caches for the life of the page, and never rejects.
 * What we borrow from core is the estimate *rules* — the part that fails silently when duplicated.
 */
@Injectable({
  providedIn: 'root'
})
export class CostEstimateHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService
  ) { }

  /**
   * Every plan available in a state, plus our own cash-pay rates. Errors strictly, so the store can
   * tell a failed request from a state with no plans.
   *
   * @param stateAbbreviation - Two-letter state abbreviation, e.g. `CO`.
   * @returns An observable of the state's plans and self-pay rates.
   */
  getStateData$(stateAbbreviation: string): Observable<IStateData> {
    const url = `${this.configurationService.env.enginelessBase}${COST_ESTIMATES_PATH}`
      + `/${encodeURIComponent(stateAbbreviation)}`;

    return this.http.get<IStateDataResponse>(url)
      .pipe(
        // Only `self_pay` needs renaming; the terse plan keys are core's contract.
        map((resp): IStateData => ({ plans: resp.plans ?? [], selfPay: resp.self_pay ?? null })),
        this.http.handleHttpErrorStrict()
      );
  }

  /**
   * The same state payload narrowed to cash-pay rates, for surfaces that never show plans.
   *
   * @param stateAbbreviation - Two-letter state abbreviation, e.g. `CO`.
   * @returns An observable of the state's self-pay rates; `plans` comes back empty.
   */
  getSelfPayData$(stateAbbreviation: string): Observable<ISelfPayRate | null> {
    const url = `${this.configurationService.env.enginelessBase}${COST_ESTIMATES_PATH}`
      + `/${encodeURIComponent(stateAbbreviation)}?view=self_pay`;

    return this.http.get<IStateDataResponse>(url)
      .pipe(
        map((resp): ISelfPayRate | null => resp.self_pay ?? null),
        this.http.handleHttpErrorStrict()
      );
  }

  /**
   * A single plan by id, for deep links that arrive with a plan already chosen.
   *
   * @param id - The plan's id.
   * @returns An observable of the plan.
   */
  getPlanById$(id: number): Observable<IPlanOption> {
    const url = `${this.configurationService.env.enginelessBase}${COST_ESTIMATES_PATH}/plans/${id}`;

    return this.http.get<IPlanOption>(url).pipe(this.http.handleHttpErrorStrict());
  }
}
